import React, { useState } from 'react';
import { 
  Search,
  X,
  SlidersHorizontal,
  Image as ImageIcon,
  Film,
  Link2,
  Folder
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCanvasStore } from '@/stores/canvasStore';
import { useIsMobile } from '@/hooks/use-mobile';

interface WallSearchWithFiltersProps {
  value: string;
  onChange: (value: string) => void;
  activeFilters: string[];
  onFiltersChange: (filters: string[]) => void;
  resultCount?: number;
  showBoardFilters: boolean;
  onToggleBoardFilters: () => void;
}

const SEARCH_FILTERS = [
  { id: 'image', label: 'Photos', icon: ImageIcon, color: '#22C55E' },
  { id: 'video', label: 'Videos', icon: Film, color: '#EF4444' },
  { id: 'link', label: 'Links', icon: Link2, color: '#EC4899' },
];

export function WallSearchWithFilters({ 
  value, 
  onChange, 
  activeFilters, 
  onFiltersChange,
  resultCount,
  showBoardFilters,
  onToggleBoardFilters
}: WallSearchWithFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);
  const { wallScrollOffset } = useCanvasStore();
  const isMobile = useIsMobile();

  const toggleFilter = (filterId: string) => {
    const newFilters = activeFilters.includes(filterId)
      ? activeFilters.filter(f => f !== filterId)
      : [...activeFilters, filterId];
    onFiltersChange(newFilters);
  };

  const clearAll = () => {
    onChange('');
    onFiltersChange([]);
  };

  const hasActiveSearch = value || activeFilters.length > 0;
  
  // Search bar becomes sticky after scrolling past ~60px
  const isSticky = wallScrollOffset > 60;

  return (
    <>
      {/* Initial spacer for nav bar */}
      <div className="h-[91px]" />
      
      {/* Sticky search bar - always same height, only background changes */}
      <div 
        className={cn(
          "sticky top-0 z-40 py-4 transition-colors duration-200",
          isSticky && "bg-background/95 backdrop-blur-xl border-b border-border/30"
        )}
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="floating-pill flex items-center gap-3 px-5 py-4 rounded-full">
            <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Search mindfull..."
              className="flex-1 bg-transparent text-lg text-foreground placeholder:text-muted-foreground focus:outline-none font-sans"
            />
            
            {/* Filter Toggle & Clear */}
            <div className="flex items-center gap-2">
              {hasActiveSearch && (
                <>
                  {resultCount !== undefined && !isMobile && (
                    <span className="text-muted-foreground text-sm">{resultCount} results</span>
                  )}
                  <button
                    onClick={clearAll}
                    className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  'relative p-2 rounded-xl transition-colors',
                  showFilters || activeFilters.length > 0
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
                title="Filter by type"
              >
                <SlidersHorizontal className="w-4 h-4" />
                {activeFilters.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                    {activeFilters.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Filter Chips - inside sticky area */}
        {showFilters && (
          <div className="max-w-3xl mx-auto px-6 pt-4">
            <div className="flex flex-wrap items-center justify-center gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
              {SEARCH_FILTERS.map(filter => {
                const Icon = filter.icon;
                const isActive = activeFilters.includes(filter.id);
                
                return (
                  <button
                    key={filter.id}
                    onClick={() => toggleFilter(filter.id)}
                    className={cn(
                      'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 floating-pill',
                      isActive
                        ? 'shadow-md border-primary/30 text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:shadow-md'
                    )}
                  >
                    <Icon 
                      className="w-4 h-4" 
                      style={{ color: filter.color }}
                    />
                    {filter.label}
                  </button>
                );
              })}
              
              {/* Boards filter toggle */}
              <button
                onClick={onToggleBoardFilters}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 floating-pill',
                  showBoardFilters
                    ? 'shadow-md border-primary/30 text-foreground bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:shadow-md'
                )}
              >
                <Folder className="w-4 h-4 text-primary" />
                Boards
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom spacer for consistent gap to content below */}
      <div className="h-4" />
    </>
  );
}
